import React, { useEffect, useRef, useState } from 'react'

import { Stack, Typography, useTheme } from '@mui/material'

import { Modal, Button, Alert, Input, TextArea } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { useSuggestIntegration } from 'hooks/api'
import { integrationRequestsApi, type SimilarRequest } from 'services'

/**
 * "Suggest an integration" form (VERA-407).
 * The user supplies just the integration's website URL (plus an optional reason);
 * the server enriches name / logo / description from the site on submit, and the
 * suggestion lands as pending_review for staff to categorize + promote on Labs.
 *
 * Two-layer dedup: Layer-2 debounced typeahead surfaces similar existing entries
 * ("Vote for it instead"); Layer-1 same-domain rejection on submit returns a
 * duplicate the user can vote for or override ("Submit as new"). Auto-votes the
 * submitter.
 */

/** Extract a search term from a URL/host: "https://www.klaviyo.com" -> "klaviyo". */
const domainLabel = (input: string): string => {
  let s = input.trim().toLowerCase()
  if (!s) return ''
  s = s.replace(/^https?:\/\//, '').replace(/^www\./, '')
  const host = s.split('/')[0]
  const parts = host.split('.').filter(Boolean)
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0] || ''
}

export const SuggestIntegrationModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const { mutateAsync, isPending } = useSuggestIntegration()

  const [url, setUrl] = useState('')
  const [reason, setReason] = useState('')
  const [similar, setSimilar] = useState<SimilarRequest[]>([])
  const [duplicate, setDuplicate] = useState<SimilarRequest | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Layer-2 typeahead: debounced search on the domain label as the user types the URL.
  useEffect(() => {
    const term = domainLabel(url)
    if (!open || term.length < 2) {
      setSimilar([])
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSimilar(await integrationRequestsApi.similar(term))
    }, 250)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [url, open])

  const reset = () => {
    setUrl('')
    setReason('')
    setSimilar([])
    setDuplicate(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const voteForExisting = async (requestId: string) => {
    try {
      await integrationRequestsApi.toggleVote(requestId)
      notification({ type: 'success', title: 'Vote recorded.' })
      handleClose()
    } catch (e: any) {
      notification({ type: 'error', title: e?.message || 'Failed to vote.' })
    }
  }

  const submit = async (forceSubmit = false) => {
    if (domainLabel(url).length < 2) {
      notification({ type: 'error', title: 'Please enter a valid website URL.' })
      return
    }
    try {
      const result = await mutateAsync({
        url: url.trim(),
        reason: reason.trim() || undefined,
        forceSubmit,
      })
      if (result.duplicateOf) {
        setDuplicate({
          id: result.duplicateOf.id,
          name: result.duplicateOf.name,
          slug: result.duplicateOf.slug,
          status: result.duplicateOf.status,
          voteCount: 0,
        })
        return
      }
      notification({ type: 'success', title: result.message || "Thanks. We'll review it and add it to the list." })
      handleClose()
    } catch (e: any) {
      notification({ type: 'error', title: e?.message || 'Failed to submit.' })
    }
  }

  return (
    <Modal
      title="Suggest an integration"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" disableElevation onClick={() => submit(false)} loading={isPending}>
            Submit
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5, pb: '20px' }}>
          Don't see what you need? Just drop the integration's website. We'll pull in the rest. Your suggestion gets your vote automatically.
        </Typography>

        <Stack spacing={1}>
          <Typography variant="body3">Website URL</Typography>
          <Input placeholder="e.g. klaviyo.com" value={url} onChange={(v) => setUrl(v)} />
        </Stack>

        {/* Layer-2: similar existing entries → vote instead */}
        {similar.length > 0 && !duplicate && (
          <Stack spacing={2} sx={{ p: 3, borderRadius: 2, backgroundColor: theme.palette.primary[50] }}>
            <Typography variant="body3" color="text.secondary">
              Already on the list. Vote instead of adding a duplicate:
            </Typography>
            {similar.map((s) => (
              <Stack key={s.id} direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body3">{s.name}</Typography>
                <Button variant="secondary" disableElevation onClick={() => voteForExisting(s.id)}>
                  Vote for it
                </Button>
              </Stack>
            ))}
          </Stack>
        )}

        <Stack spacing={1}>
          <Typography variant="body3">Why do you want this? (optional)</Typography>
          <TextArea
            minRows={3}
            placeholder="Please tell us why you want this integration"
            value={reason}
            onChange={(v) => setReason(v)}
          />
        </Stack>

        {/* Layer-1: same-domain duplicate rejection → vote instead / submit as new */}
        {duplicate && (
          <Alert
            severity="info"
            description={`"${duplicate.name}" is already on the list. Vote for it, or submit as new if it's a different tool.`}
          />
        )}
        {duplicate && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="secondary" disableElevation onClick={() => submit(true)} loading={isPending}>
              Submit as new
            </Button>
            <Button variant="primary" disableElevation onClick={() => voteForExisting(duplicate.id)}>
              Vote for it instead
            </Button>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
